import { type WebRtcConnectionStep, waitForIceConnected } from "./connection-steps.js";
import { createListenerConnection } from "./peer.js";
import { createStepTimer, type StepTimings } from "./timings.js";
import { waitForRemoteAudioTracks } from "./track-wait.js";
import type { CallsPublishedTrack } from "./tracks.js";

export interface BroadcastStateForEgress {
  isLive: boolean;
  isDemo: boolean;
  sessionId: string;
  trackId: string;
}

export interface SubscribeResult {
  sessionId: string;
  requiresImmediateRenegotiation?: boolean;
  sessionDescription?: { sdp: string; type: RTCSdpType };
  tracks?: CallsPublishedTrack[];
}

export interface EgressApi {
  /**
   * Create the listener Session and subscribe it to the live Broadcast in a
   * single request. The server chains `sessions/new` and `tracks/new` without
   * leaving Cloudflare, removing a client round trip before audio.
   */
  subscribe(): Promise<SubscribeResult>;
  renegotiateSession(sessionId: string, sdpAnswer: string): Promise<unknown>;
}

export interface EgressPipelineOptions {
  onLog?: (message: string) => void;
  onStep?: (step: WebRtcConnectionStep) => void;
  onConnectionState?: (state: RTCPeerConnectionState) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  /**
   * Pre-created (and ideally gesture-primed) audio element to reuse instead of
   * creating a fresh one. Lets callers unlock iOS playback within the tap.
   */
  audioElement?: HTMLAudioElement;
}

export interface EgressPipelineResult {
  listenerSessionId: string;
  peerConnection: RTCPeerConnection;
  audioElement: HTMLAudioElement;
  timings: StepTimings;
}

function trackMidsFromResponse(tracks: CallsPublishedTrack[] | undefined): string[] {
  if (!tracks?.length) {
    return [];
  }
  return tracks.map((track) => track.mid).filter((mid): mid is string => mid != null && mid !== "");
}

/**
 * Tuner listener egress — Cloudflare subscriber (pull) flow:
 * subscribe (session + track, server-side) → renegotiate answer → wait for ICE + tracks.
 *
 * The listener deliberately sends no offer of its own. Measured against the
 * production SFU, offering at session creation makes the whole join about four
 * times slower, because `tracks/new` then has to renegotiate a session that
 * already has a PeerConnection attached.
 */
export async function runEgressPipeline(
  api: EgressApi,
  broadcastState: BroadcastStateForEgress,
  options: EgressPipelineOptions = {},
): Promise<EgressPipelineResult> {
  if (!broadcastState.isLive) {
    throw new Error("Cannot connect: No active live stream detected.");
  }

  const log = options.onLog ?? (() => {});
  const timer = createStepTimer();
  const step = (next: WebRtcConnectionStep): void => {
    timer.mark(next);
    options.onStep?.(next);
  };

  step("peer");
  log("Creating listener peer connection to Cloudflare Realtime...");
  const peerConnection = createListenerConnection();

  let audioElement = options.audioElement;
  if (!audioElement) {
    audioElement = document.createElement("audio");
    audioElement.autoplay = true;
    audioElement.setAttribute("playsinline", "");
  }

  if (options.onConnectionState) {
    peerConnection.onconnectionstatechange = () => {
      options.onConnectionState?.(peerConnection.connectionState);
    };
  }

  step("subscribe");
  const trackWaitAbort = new AbortController();
  try {
    log("Creating listener session and subscribing to the live track...");
    const trackData = await api.subscribe();
    const listenerSessionId = trackData.sessionId;
    log(`Egress session spawned on SFU: ${listenerSessionId}`);

    const expectedMids = trackMidsFromResponse(trackData.tracks);
    const trackWaiters = waitForRemoteAudioTracks(peerConnection, expectedMids, {
      onLog: log,
      signal: trackWaitAbort.signal,
    });
    // Swallow the rejection if we bail (and abort) before awaiting below,
    // so an aborted wait never surfaces as an unhandled rejection.
    void trackWaiters.catch(() => {});

    if (trackData.requiresImmediateRenegotiation && trackData.sessionDescription) {
      step("renegotiate");
      log("SFU requested renegotiation. Applying remote offer...");
      await peerConnection.setRemoteDescription(trackData.sessionDescription);

      log("Generating local SDP renegotiation answer...");
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      // The SFU sends no RTP for a newly pulled track until it has this answer,
      // so this call is genuinely on the audio path and cannot be deferred.
      log("Completing renegotiation handshake...");
      await api.renegotiateSession(listenerSessionId, answer.sdp!);
      log("Renegotiation complete.");
    } else if (trackData.sessionDescription) {
      log("Applying session description from tracks/new (no renegotiation flag)...");
      await peerConnection.setRemoteDescription(trackData.sessionDescription);
    }

    step("stream");
    log("Waiting for remote audio track(s)...");
    const pulledTracks = await trackWaiters;
    log(`Received ${pulledTracks.length} remote audio track(s).`);

    const stream = new MediaStream();
    for (const track of pulledTracks) {
      stream.addTrack(track);
    }
    audioElement.srcObject = stream;
    options.onRemoteStream?.(stream);
    log("Linked remote track(s) to audio element output.");

    log("Waiting for ICE connected...");
    await waitForIceConnected(peerConnection);
    step("connected");

    const timings = timer.finish();
    log(`Egress timings — ${timer.summary()}`);

    return { listenerSessionId, peerConnection, audioElement, timings };
  } catch (error) {
    log(
      `Egress failed (signaling=${peerConnection.signalingState} ice=${peerConnection.iceConnectionState} conn=${peerConnection.connectionState}): ${error instanceof Error ? error.message : String(error)}`,
    );
    trackWaitAbort.abort();
    peerConnection.close();
    throw error;
  }
}

/** Tear down egress resources. */
export function cleanupEgressPipeline(
  result: Pick<EgressPipelineResult, "peerConnection" | "audioElement">,
): void {
  result.audioElement.pause();
  result.audioElement.srcObject = null;
  result.audioElement.remove();
  result.peerConnection.close();
}
